import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Database, CheckCircle, Zap, Award, ArrowRight } from 'lucide-react';

export const VehicleDataFeatureSection = ({ onCtaClick }) => {
  return (
    <section className="py-20 px-4" style={{ backgroundColor: '#0B0F14', isolation: 'isolate', position: 'relative', zIndex: 1 }}>
      <div className="max-w-7xl mx-auto">
        {/* Hero Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" 
               style={{ backgroundColor: '#1EA0E620', border: '1px solid #1EA0E6' }}>
            <Zap size={20} style={{ color: '#1EA0E6' }} />
            <span className="text-sm font-semibold" style={{ color: '#7CD4FD' }}>
              NEU: KI-gestützte Fahrzeugdaten-Bibliothek
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Unbezahlbares Wissen.
            <br />
            <span style={{ color: '#1EA0E6' }}>Immer griffbereit.</span>
          </h2>
          
          <p className="text-xl max-w-3xl mx-auto" style={{ color: '#94A3B8' }}>
            Stellen Sie sich vor: <strong style={{ color: '#E6EDF3' }}>Jede technische Information</strong> die Sie für Ihr Fahrzeug brauchen – 
            von Motoröl-Spezifikationen bis zu exakten Sensor-Positionen – <strong style={{ color: '#E6EDF3' }}>sofort verfügbar</strong>. 
            Kein stundenlanges Suchen mehr.
          </p>
        </div>

        {/* Main Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Card 1 */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', borderLeft: '4px solid #1EA0E6' }}>
            <CardContent className="p-8">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" 
                   style={{ backgroundColor: '#1EA0E620' }}>
                <Database size={24} style={{ color: '#1EA0E6' }} />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#E6EDF3' }}>
                1000+ Verifizierte Fahrzeugdaten
              </h3>
              <p className="mb-4" style={{ color: '#94A3B8' }}>
                Unsere KI durchsucht kontinuierlich das gesamte Internet nach technischen Daten. 
                <strong style={{ color: '#E6EDF3' }}> Drei verschiedene KI-Systeme</strong> prüfen jede Information gegenseitig auf Korrektheit.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Motoröl & Füllmengen:</strong> Exakte Kapazität, Viskosität, Spezifikation
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Alle Drehmomente:</strong> Radschrauben, Zylinderkopf, Kurbelwelle – mehrstufige Anzüge inklusive
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Sensor-Positionen:</strong> "Kühlmitteltemp.-Sensor: Am Thermostat, Motorblock links oben"
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Sollwerte:</strong> Rail-Druck, Injektoren, Lambda-Sonden – alles mit Toleranzen
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Card 2 */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', borderLeft: '4px solid #22C55E' }}>
            <CardContent className="p-8">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4\" 
                   style={{ backgroundColor: '#22C55E20' }}>
                <Award size={24} style={{ color: '#22C55E' }} />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#E6EDF3' }}>
                Mehr als nur Grunddaten
              </h3>
              <p className="mb-4" style={{ color: '#94A3B8' }}>
                Während andere nur <strong style={{ color: '#EF4444' }}>Basis-Informationen</strong> bieten, 
                geben wir Ihnen das <strong style={{ color: '#22C55E' }}>komplette Paket</strong> – wie ein digitales Werkstatt-Handbuch.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Klimaanlage:</strong> Kältemittel-Typ (R134a/R1234yf) + genaue Füllmenge in Gramm
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Fahrwerk:</strong> Sturz, Spur, Nachlauf – Einstellwerte für Achsvermessung
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Elektrik:</strong> Batterie-Kapazität, Generator-Leistung, Sicherungsübersicht
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: '#E6EDF3' }}>
                    <strong>Bremsen:</strong> Min/Max Scheibenstärke, Belag-Mindeststärke, Sattel-Drehmomente
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* How it Works */}
        <Card className="mb-12" style={{ backgroundColor: '#1EA0E605', borderColor: '#1EA0E6' }}>
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: '#E6EDF3' }}>
              🔄 So funktioniert's
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" 
                     style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF', fontSize: '20px', fontWeight: 'bold' }}>
                  1
                </div>
                <p className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>KI durchsucht das Web</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  8 verschiedene Such-Queries pro Fahrzeug
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" 
                     style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF', fontSize: '20px', fontWeight: 'bold' }}>
                  2
                </div>
                <p className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>Triple-AI Validierung</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  3 KI-Systeme prüfen gegenseitig
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" 
                     style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF', fontSize: '20px', fontWeight: 'bold' }}>
                  3
                </div>
                <p className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>Auto-Verifizierung</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  Bei >80% Konsens: Sofort freigegeben
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" 
                     style={{ backgroundColor: '#22C55E', color: '#FFFFFF', fontSize: '20px', fontWeight: 'bold' }}>
                  ✓
                </div>
                <p className="font-semibold mb-2" style={{ color: '#E6EDF3' }}>Sofort verfügbar</p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  In Diagnose & Bibliothek
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Value Proposition */}
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold mb-4" style={{ color: '#E6EDF3' }}>
            Warum ist das ein <span style={{ color: '#1EA0E6' }}>Must-Have</span>?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div>
              <div className="text-4xl font-bold mb-2" style={{ color: '#1EA0E6' }}>2-3 Std.</div>
              <p className="text-sm" style={{ color: '#94A3B8' }}>
                Zeitersparnis pro Reparatur durch sofort verfügbare Daten
              </p>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2" style={{ color: '#22C55E' }}>100%</div>
              <p className="text-sm" style={{ color: '#94A3B8' }}>
                Korrekte Sollwerte = Keine Folgeschäden durch falsche Anzugsmomente
              </p>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2" style={{ color: '#F59E0B' }}>€€€</div>
              <p className="text-sm" style={{ color: '#94A3B8' }}>
                Professionelle Datenbank-Abos kosten 500-1000€/Jahr – hier inklusive!
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div 
            className="rounded-2xl p-12 relative overflow-hidden"
            style={{ 
              background: 'linear-gradient(135deg, #1EA0E6 0%, #0E7FC2 50%, #1EA0E6 100%)',
              boxShadow: '0 20px 60px rgba(30, 160, 230, 0.3)',
            }}
          >
            {/* Animated Background Elements */}
            <div 
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)',
                backgroundSize: '50px 50px'
              }}
            />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" 
                   style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                <Award size={20} style={{ color: '#FFFFFF' }} />
                <span className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                  Premium Feature
                </span>
              </div>
              
              <h3 className="text-4xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
                Bereit für unbegrenztes Fachwissen?
              </h3>
              <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Upgraden Sie jetzt und erhalten Sie sofortigen Zugriff auf 1000+ verifizierte Fahrzeugdaten
              </p>
              
              <Button
                onClick={onCtaClick}
                size="lg"
                className="transform hover:scale-105 transition-transform duration-200"
                style={{ 
                  backgroundColor: '#FFFFFF', 
                  color: '#1EA0E6', 
                  fontSize: '18px', 
                  padding: '16px 48px',
                  fontWeight: '600',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
                }}
              >
                Jetzt upgraden <ArrowRight className="ml-2" size={20} />
              </Button>
              
              <div className="flex items-center justify-center gap-8 mt-8 flex-wrap">
                <div className="flex items-center gap-2">
                  <CheckCircle size={20} style={{ color: '#FFFFFF' }} />
                  <span className="text-sm" style={{ color: '#FFFFFF' }}>Sofortiger Zugriff</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={20} style={{ color: '#FFFFFF' }} />
                  <span className="text-sm" style={{ color: '#FFFFFF' }}>Keine Einrichtungsgebühr</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={20} style={{ color: '#FFFFFF' }} />
                  <span className="text-sm" style={{ color: '#FFFFFF' }}>Jederzeit kündbar</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
